import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { FicheCard } from "@/components/FicheCard";
import { PaywallDialog } from "@/components/PaywallDialog";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ScanLine, Upload, Camera, ArrowLeft, Loader2, FileText } from "lucide-react";

export default function Scanner() {
  const { user, refresh } = useAuth();
  const location = useLocation();
  const fileRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fiche, setFiche] = useState(null);
  const [docId, setDocId] = useState(null);
  const [history, setHistory] = useState([]);
  const [paywall, setPaywall] = useState(false);
  const [paywallReason, setPaywallReason] = useState("");

  const loadHistory = () => api.get("/documents").then((r) => setHistory(r.data)).catch(() => {});

  useEffect(() => {
    loadHistory();
    if (location.state?.docId) {
      setDocId(location.state.docId);
      api.get(`/documents/${location.state.docId}`).then((r) => setFiche(r.data.fiche)).catch(() => {});
    }
  }, []); // eslint-disable-line

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPreview(URL.createObjectURL(file));
    setFiche(null);
    setLoading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/documents/scan", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setFiche(data.fiche);
      setDocId(data.id);
      toast.success("Fiche ADMINIA prête !");
      loadHistory();
      refresh();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 402) {
        setPaywallReason(detail?.message || "Limite atteinte.");
        setPaywall(true);
      } else {
        toast.error(formatApiError(detail) || "L'analyse a échoué.");
      }
    } finally {
      setLoading(false);
    }
  };

  const speakBlocked = (reason) => {
    setPaywallReason(reason || "La lecture à voix haute est réservée aux abonnés Premium.");
    setPaywall(true);
  };

  const reset = () => { setFiche(null); setPreview(null); setDocId(null); };

  return (
    <div className="space-y-6 adminia-fade-in">
      <PaywallDialog open={paywall} onOpenChange={setPaywall} reason={paywallReason} />
      <input ref={fileRef} type="file" accept="image/*" capture="environment"
        className="hidden" onChange={onFile} data-testid="scan-file-input" />

      {!fiche && !loading && (
        <>
          <div>
            <h1 className="font-heading font-bold text-2xl sm:text-3xl">Scanner un courrier</h1>
            <p className="text-muted-foreground mt-1">
              Prenez une photo nette du courrier, bien à plat et lisible.
            </p>
          </div>

          <Card className="p-8 border-2 border-dashed border-brand/30 bg-surface text-center">
            {preview ? (
              <img src={preview} alt="aperçu" className="max-h-64 mx-auto rounded-lg mb-4" />
            ) : (
              <div className="h-20 w-20 rounded-2xl bg-brand/10 flex items-center justify-center mx-auto mb-4">
                <ScanLine className="h-10 w-10 text-brand" />
              </div>
            )}
            <p className="font-medium mb-5">Ajoutez la photo de votre courrier</p>
            <div className="flex flex-wrap gap-3 justify-center">
              <Button className="rounded-full bg-brand hover:bg-brand/90 gap-2 h-12 px-6"
                onClick={() => fileRef.current?.click()} data-testid="scan-camera-btn">
                <Camera className="h-5 w-5" /> Prendre / choisir une photo
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-4">Formats acceptés : JPG, PNG, WEBP</p>
          </Card>

          {history.length > 0 && (
            <div>
              <h2 className="font-heading font-semibold text-lg mb-3">Mes analyses</h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {history.map((d) => (
                  <Card key={d.id} className="p-4 hover:shadow-md hover:border-brand/40 transition-all cursor-pointer"
                    onClick={() => { setFiche(d.fiche); setDocId(d.id); }} data-testid={`history-item-${d.id}`}>
                    <div className="flex items-start gap-3">
                      <FileText className="h-5 w-5 text-brand shrink-0 mt-0.5" />
                      <div className="min-w-0">
                        <p className="font-medium text-sm truncate">{d.titre}</p>
                        <p className="text-xs text-muted-foreground truncate">{d.expediteur}</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {new Date(d.created_at).toLocaleDateString("fr-FR")}
                        </p>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {loading && (
        <Card className="p-12 text-center">
          {preview && <img src={preview} alt="aperçu" className="max-h-56 mx-auto rounded-lg mb-6 opacity-60" />}
          <Loader2 className="h-10 w-10 text-brand mx-auto animate-spin mb-4" />
          <p className="font-heading font-semibold text-lg">Analyse en cours…</p>
          <p className="text-muted-foreground text-sm mt-1">ADMINIA lit votre courrier attentivement.</p>
        </Card>
      )}

      {fiche && !loading && (
        <>
          <Button variant="ghost" onClick={reset} className="gap-2 -ml-2" data-testid="scan-back-btn">
            <ArrowLeft className="h-4 w-4" /> Nouveau scan
          </Button>
          <FicheCard fiche={fiche} docId={docId} isPremium={user?.is_premium} onPremiumBlocked={speakBlocked} />
        </>
      )}
    </div>
  );
}
